import { ReactNode, useEffect, useState } from 'react';

import cn from 'classnames';

import { dispatchHoldingApi } from 'actions/other';
import { setAccountRefreshTriggerLastClicked } from 'actions/user';
import Render from 'components/render';
import {
  ACCOUNT_REFRESH_TRIGGER_LAST_CLICKED_COOLDOWN_TIME,
  ACCOUNT_REFRESH_TRIGGER_LAST_CLICKED_LOADING_ANIMATION_TIME,
} from 'constants/constants';
import { timeNow } from 'helpers/day';
import i18n from 'i18n/config';
import { lastHoldingApiStatusSelector } from 'selectors/holdingsSelectors';
import { selectAccountRefreshTriggerLastClicked } from 'selectors/user';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { WarningAccountIcon } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import styles from './accountRefreshTrigger.module.scss';

interface AccountRefreshTriggerProps {
  className?: string;
  message: string | ReactNode;
  isCtaVisible?: boolean;
}

const refreshLabel = i18n.t('common:refresh');

const AccountRefreshTrigger = ({
  className = '',
  message,
  isCtaVisible = true,
}: AccountRefreshTriggerProps) => {
  const appDispatch = useAppDispatch();
  const [isLoadingAnimationTriggered, setLoadingAnimationTriggered] =
    useState<boolean>(false);

  const accountRefreshTriggerLastClicked = useAppSelector(
    selectAccountRefreshTriggerLastClicked
  );

  const lastHoldingApiStatus = useAppSelector(lastHoldingApiStatusSelector);

  const onHandleRefreshClick = () => {
    const now = timeNow().valueOf();

    // Case 1: user clicks inside cooldown => fake loader
    if (
      accountRefreshTriggerLastClicked &&
      now - accountRefreshTriggerLastClicked <
        ACCOUNT_REFRESH_TRIGGER_LAST_CLICKED_COOLDOWN_TIME
    ) {
      setLoadingAnimationTriggered(true);
      setTimeout(
        () => setLoadingAnimationTriggered(false),
        ACCOUNT_REFRESH_TRIGGER_LAST_CLICKED_LOADING_ANIMATION_TIME
      );
      // don’t dispatch API
      return;
    }

    // Case 2: user clicks after cooldown => real API loader
    setLoadingAnimationTriggered(true);
    appDispatch(setAccountRefreshTriggerLastClicked(now));
    appDispatch(dispatchHoldingApi({ isUserTriggered: true }));
  };

  // stop loader - for real API loader stop
  useEffect(() => {
    if (
      lastHoldingApiStatus?.isUserTriggered &&
      lastHoldingApiStatus?.pendingCount === 0
    ) {
      setLoadingAnimationTriggered(false);
    }
  }, [lastHoldingApiStatus]);

  return (
    <Box className={cn(styles.container, className)}>
      <Box className={styles.iconMessageContainer}>
        <WarningAccountIcon />
        <Box as="span" className={styles.message}>
          {message}
        </Box>
      </Box>
      <Render when={isCtaVisible}>
        <Box className={styles.ctaContainer}>
          <DeltaButton
            onClick={onHandleRefreshClick}
            data-testid="account-refresh-trigger-cta"
            className={styles.cta}
            loading={isLoadingAnimationTriggered}
          >
            {refreshLabel}
          </DeltaButton>
        </Box>
      </Render>
    </Box>
  );
};

AccountRefreshTrigger.displayName = 'AccountRefreshTrigger';

export { AccountRefreshTrigger };
